import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Logo from './ui/Logo';
import { calendlyLinkProps, openCalendly } from '../lib/calendly';

const navLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Work', href: '#work' },
    { name: 'Process', href: '#process' },
    { name: 'FAQ', href: '#faq' },
];

const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeId, setActiveId] = useState<string>('');

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll-spy: highlight the section currently in view.
    useEffect(() => {
        const ids = navLinks.map((l) => l.href.slice(1));
        const sections = ids
            .map((id) => document.getElementById(id))
            .filter((el): el is HTMLElement => Boolean(el));

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveId(entry.target.id);
                });
            },
            { rootMargin: '-45% 0px -50% 0px' }
        );
        sections.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, []);

    // Lock scroll while the mobile menu is open.
    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                isScrolled
                    ? 'bg-navy-950/70 backdrop-blur-xl border-b border-white/[0.06] py-3'
                    : 'bg-transparent py-5'
            }`}
        >
            <nav className="container-x flex items-center justify-between">
                <Logo />

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-1 rounded-full glass px-2 py-1.5">
                    {navLinks.map((link) => {
                        const isActive = activeId === link.href.slice(1);
                        return (
                            <a
                                key={link.name}
                                href={link.href}
                                className={`relative px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive ? 'text-white' : 'text-text-muted hover:text-white'
                                }`}
                            >
                                {isActive && (
                                    <motion.span
                                        layoutId="nav-pill"
                                        className="absolute inset-0 rounded-full bg-white/[0.08] border border-white/[0.08]"
                                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                    />
                                )}
                                <span className="relative">{link.name}</span>
                            </a>
                        );
                    })}
                </div>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        {...calendlyLinkProps}
                        className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-navy-900 text-sm font-semibold hover:bg-brand-cyan transition-colors"
                    >
                        Book a call
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                </div>

                {/* Mobile toggle */}
                <button
                    className="md:hidden w-10 h-10 grid place-items-center rounded-lg text-white glass"
                    onClick={() => setIsMobileMenuOpen((v) => !v)}
                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMobileMenuOpen}
                >
                    {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </nav>

            {/* Mobile menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="md:hidden absolute top-full inset-x-0 mx-4 mt-2 rounded-2xl bg-navy-900/95 backdrop-blur-xl border border-white/[0.08] p-4 flex flex-col gap-1 shadow-lift"
                    >
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                className="px-4 py-3 rounded-xl text-text-main font-medium hover:bg-white/[0.05] transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {link.name}
                            </a>
                        ))}
                        <a
                            {...calendlyLinkProps}
                            className="mt-2 btn-primary w-full"
                            onClick={(e) => {
                                openCalendly(e);
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            Book a free consultation
                            <ArrowRight className="w-4 h-4" />
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
